import { useEffect, useState } from 'react';
// @ts-ignore: no def file
import { RouteComponentProps } from 'react-router';
import { Event } from '../../../../dorian-shared/types/event/Event';
import { DetailedPrize } from '../components/EventForm/DetailedPrizes/DetailedPrizeType';
import { EventFormValues, createInitialFormValues, eventToFormValues } from '../components/EventForm/EventForm';
import { FileInputValue } from '../components/ImageInput/ImageInput';

interface DuplicateLocationState {
  duplicateEvent?: Event;
  duplicatePrizes?: DetailedPrize[];
}

function extractFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/').pop() || 'image.jpg';
  } catch {
    return 'image.jpg';
  }
}

async function fetchImageAsFileInput(imageUrl: string): Promise<FileInputValue> {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  return {
    fileName: extractFileNameFromUrl(imageUrl),
    file: blob,
  };
}

export function useDuplicateFormValues(location: RouteComponentProps['location']) {
  const state = location.state as DuplicateLocationState | undefined;
  const isDuplicate = Boolean(state?.duplicateEvent);

  const [formValues, setFormValues] = useState<EventFormValues>(
    () => {
      if (!state?.duplicateEvent) return createInitialFormValues();
      const { id, ...eventWithoutId } = state.duplicateEvent;
      const prizes = (state.duplicatePrizes ?? []).map(({ id: _id, ...rest }) => rest);
      return eventToFormValues(eventWithoutId, prizes as DetailedPrize[]);
    },
  );
  const [isPreparing, setIsPreparing] = useState(isDuplicate);

  useEffect(() => {
    if (!isDuplicate || !state?.duplicateEvent) return;

    let cancelled = false;
    const prizes = state.duplicatePrizes ?? [];

    const coverPromise = state.duplicateEvent.imageUrl
      ? fetchImageAsFileInput(state.duplicateEvent.imageUrl)
      : Promise.resolve(undefined);

    const prizePromises = prizes.map((prize) => (
      prize.imageUrl
        ? fetchImageAsFileInput(prize.imageUrl).catch(() => undefined)
        : Promise.resolve(undefined)
    ));

    Promise.all([coverPromise, ...prizePromises])
      .then(([coverImage, ...prizeImages]) => {
        if (cancelled) return;
        setFormValues((prev) => ({
          ...prev,
          newCoverImage: coverImage,
          detailedEventPrizes: prev.detailedEventPrizes.map((prize, i) => ({
            ...prize,
            image: prizeImages[i],
          })),
        }));
      })
      .catch(() => { /* user can upload manually */ })
      .finally(() => {
        if (!cancelled) setIsPreparing(false);
      });

    return () => { cancelled = true; };
  }, [isDuplicate, state]);

  return { formValues, isPreparing };
}
